import {
    ACCOUNT_SIGNALS_MANAGEMENT_PAGE_ROUTE,
    ACCOUNT_SIGNALS_MANAGEMENT_PAGE_URL,
    HOME_PAGE_ROUTE,
    INTENT_SIGNALS_MANAGEMENT_INTRO_PAGE_ROUTE,
    INTENT_SIGNALS_MANAGEMENT_INTRO_PAGE_URL,
    INTENT_SIGNALS_MANAGEMENT_PAGE_ROUTE,
    INTENT_SIGNALS_MANAGEMENT_PAGE_URL,
    MANAGEMENT_ROUTE,
    SIGNAL_ANALYSIS_MANAGEMENT_PAGE_ROUTE,
    SIGNAL_ANALYSIS_MANAGEMENT_PAGE_URL,
    SI_ROOT_ROUTE,
} from "app/pages/sales-intelligence/constants/routes";
import {
    MANAGEMENT_SAVED_SEARCH_WEBHOOKS_ROUTE,
    MANAGEMENT_SIGNALS_API_ROUTE,
    MANAGEMENT_SIGNALS_API_URL,
    MANAGEMENT_SIGNALS_REST_API_ROUTE,
    MANAGEMENT_SIGNALS_REST_API_URL,
    SAVED_SEARCH_WEBHOOKS_MANAGEMENT_URL,
} from "app/pages/sales-intelligence/constants/sl_routes";
import ManageIntentSignalsPageContainer from "app/pages/sales-intelligence/pages/manage-signals/ManageIntentSignalsPageContainer";
import ManageSignalsPageContainer from "app/pages/sales-intelligence/pages/manage-signals/ManageSignalsPageContainer";
import SignalAnalysisPageContainer from "app/pages/sales-intelligence/pages/manage-signals/SignalAnalysisPageContainer";
import SavedSearchWebhooksManagementContainer from "app/pages/sales-intelligence/pages/management/containers/SavedSearchWebhooksManagementContainer/SavedSearchWebhooksManagementContainer";
import SignalsManagementIntegrationContainer from "app/pages/sales-intelligence/pages/management/containers/SignalsManagementIntegrationContainer/SignalsManagementIntegrationContainer";
import SignalsRestApiManagementContainer from "app/pages/sales-intelligence/pages/management/containers/SignalsRestApiManagementContainer/SignalsRestApiManagementContainer";
import {
    canManageSavedSearchWebhooks,
    canManageSignalsApi,
} from "app/pages/sales-intelligence/modules/manage-integrations/utils/managementAccess";
import { canAccessSignalAnalysis } from "app/pages/sales-intelligence/modules/signal-recommender/utils/signalAnalysisAccess";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type { SecondaryBarType } from "app/reducers/_reducers/SecondaryBarReducer";
import { salesRouteGate } from "single-spa/react-app/config/solutions25/sales/utils/salesRouteGate";
import type { RouterStateConfig } from "single-spa/react-app/router/IRouterState";

const canManageIntentSignals = (): boolean => salesSettingsHelper.isIntentSignalsEnabled();

export const signalsManagementConfig: RouterStateConfig = {
    [ACCOUNT_SIGNALS_MANAGEMENT_PAGE_ROUTE]: {
        configId: "SalesWorkspace",
        parent: SI_ROOT_ROUTE,
        stateId: ACCOUNT_SIGNALS_MANAGEMENT_PAGE_ROUTE,
        pageTitle: "si.pages.signals_management.document_title",
        url: ACCOUNT_SIGNALS_MANAGEMENT_PAGE_URL,
        component: ManageSignalsPageContainer,
        trackingId: {
            section: "Manage_signals",
            subSection: "Manage_admin",
            subSubSection: "Signals_management",
        },
        pageId: {
            section: "Manage_signals",
            subSection: "Manage_admin",
            subSubSection: "Signals_management",
        },
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
    },
    [INTENT_SIGNALS_MANAGEMENT_PAGE_ROUTE]: {
        configId: "SalesWorkspace",
        parent: SI_ROOT_ROUTE,
        stateId: INTENT_SIGNALS_MANAGEMENT_PAGE_ROUTE,
        pageTitle: "si.pages.intent.document_title",
        url: INTENT_SIGNALS_MANAGEMENT_PAGE_URL,
        component: ManageIntentSignalsPageContainer,
        trackingId: {
            section: "Intent_signals",
            subSection: "Intent_admin",
            subSubSection: "Intent_management",
        },
        pageId: {
            section: "Intent_signals",
            subSection: "Intent_admin",
            subSubSection: "Intent_management",
        },
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        isHidden: (): boolean => !canManageIntentSignals(),
        redirectTo: salesRouteGate(canManageIntentSignals, ACCOUNT_SIGNALS_MANAGEMENT_PAGE_ROUTE),
    },
    [INTENT_SIGNALS_MANAGEMENT_INTRO_PAGE_ROUTE]: {
        configId: "SalesWorkspace",
        parent: SI_ROOT_ROUTE,
        stateId: INTENT_SIGNALS_MANAGEMENT_INTRO_PAGE_ROUTE,
        pageTitle: "si.pages.intent.document_title",
        url: INTENT_SIGNALS_MANAGEMENT_INTRO_PAGE_URL,
        component: ManageIntentSignalsPageContainer,
        trackingId: {
            section: "Intent_signals",
            subSection: "Intent_admin",
            subSubSection: "Intent_intro",
        },
        pageId: {
            section: "Intent_signals",
            subSection: "Intent_admin",
            subSubSection: "Intent_intro",
        },
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        isHidden: (): boolean => !canManageIntentSignals(),
        redirectTo: salesRouteGate(canManageIntentSignals, ACCOUNT_SIGNALS_MANAGEMENT_PAGE_ROUTE),
    },
    [SIGNAL_ANALYSIS_MANAGEMENT_PAGE_ROUTE]: {
        configId: "SalesWorkspace",
        parent: SI_ROOT_ROUTE,
        stateId: SIGNAL_ANALYSIS_MANAGEMENT_PAGE_ROUTE,
        pageTitle: "si.pages.signal-recommender.title",
        url: SIGNAL_ANALYSIS_MANAGEMENT_PAGE_URL,
        component: SignalAnalysisPageContainer,
        trackingId: {
            section: "Manage_signals",
            subSection: "Manage_admin",
            subSubSection: "Signal_analysis",
        },
        pageId: {
            section: "Manage_signals",
            subSection: "Manage_admin",
            subSubSection: "Signal_analysis",
        },
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        isHidden: () => !canAccessSignalAnalysis(),
        redirectTo: salesRouteGate(canAccessSignalAnalysis, ACCOUNT_SIGNALS_MANAGEMENT_PAGE_ROUTE),
    },
    [MANAGEMENT_SIGNALS_API_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: MANAGEMENT_SIGNALS_API_URL,
        configId: "SalesWorkspace",
        stateId: MANAGEMENT_SIGNALS_API_ROUTE,
        component: SignalsManagementIntegrationContainer,
        pageTitle: "si.pages.management.document_title",
        trackingId: {
            section: "SI_admin",
            subSection: "integrations",
            subSubSection: "Manage signals API",
        },
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceManagement" as SecondaryBarType,
        isHidden: (): boolean => !canManageSignalsApi(),
        redirectTo: salesRouteGate(canManageSignalsApi, MANAGEMENT_ROUTE),
    },
    [MANAGEMENT_SIGNALS_REST_API_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: MANAGEMENT_SIGNALS_REST_API_URL,
        configId: "SalesWorkspace",
        stateId: MANAGEMENT_SIGNALS_REST_API_ROUTE,
        component: SignalsRestApiManagementContainer,
        pageTitle: "si.pages.management.document_title",
        trackingId: {
            section: "SI_admin",
            subSection: "integrations",
            subSubSection: "Manage Signals REST API",
        },
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceManagement" as SecondaryBarType,
    },
    [MANAGEMENT_SAVED_SEARCH_WEBHOOKS_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: SAVED_SEARCH_WEBHOOKS_MANAGEMENT_URL,
        configId: "SalesWorkspace",
        stateId: MANAGEMENT_SAVED_SEARCH_WEBHOOKS_ROUTE,
        component: SavedSearchWebhooksManagementContainer,
        pageTitle: "si.pages.management.document_title",
        trackingId: {
            section: "SI_admin",
            subSection: "integrations",
            subSubSection: "Manage saved search webhooks",
        },
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceManagement" as SecondaryBarType,
        isHidden: (): boolean => !canManageSavedSearchWebhooks(),
        redirectTo: salesRouteGate(canManageSavedSearchWebhooks, MANAGEMENT_ROUTE),
    },
};
